import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';

import { useDebounce } from '@apps/web/hooks/use_debounce';
import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

export function useCoverageShiftAvailability(
    organizationId: string | null,
    shiftIds: ReadonlyArray<string>,
    caregiverIds: ReadonlyArray<string>,
) {
    const mode = useMode();
    const idsKey = [...caregiverIds].sort().join(',');
    const shiftsKey = [...shiftIds].sort().join(',');
    const settledShifts = useDebounce(shiftsKey, 200);
    const settledIds = useDebounce(idsKey, 200);
    const waiting = idsKey !== settledIds || shiftsKey !== settledShifts;
    const requestKey = JSON.stringify([
        organizationId,
        mode,
        shiftsKey,
        idsKey,
    ]);
    const activeRequestRef = useRef<string | null>(requestKey);
    useEffect(() => {
        activeRequestRef.current = requestKey;
        return () => {
            activeRequestRef.current = null;
        };
    }, [requestKey]);
    const query = useSWR(
        !waiting && organizationId && settledShifts && settledIds
            ? ([
                  'coverage-shift-availability',
                  organizationId,
                  mode,
                  settledShifts,
                  settledIds,
              ] as const)
            : null,
        async ([, orgId, requestMode, selectedShifts, ids]) => {
            const fetchKey = JSON.stringify([
                orgId,
                requestMode,
                selectedShifts,
                ids,
            ]);
            const caregiverIds = ids.split(',');
            const statuses = new Map<
                string,
                WorkforceMapPoint['availability_status']
            >();
            const omittedIds = new Set<string>();
            const priority = {
                available: 0,
                unknown: 1,
                unavailable: 2,
                scheduled: 3,
            };
            for (const selectedShift of selectedShifts.split(',')) {
                for (let start = 0; start < caregiverIds.length; start += 100) {
                    const response = await getApiClient().post({
                        url: '/organizations/{organization_id}/coverage/map/shifts/{shift_id}/availability',
                        pathParams: {
                            organization_id: orgId,
                            shift_id: selectedShift,
                        },
                        mode: requestMode,
                        body: {
                            caregiver_ids: caregiverIds.slice(
                                start,
                                start + 100,
                            ),
                        },
                        idempotent: true,
                        reason: 'coverage-shift-availability',
                    });
                    if (activeRequestRef.current !== fetchKey) {
                        throw new DOMException(
                            'Availability selection changed',
                            'AbortError',
                        );
                    }
                    const shiftStatuses = new Map(
                        response.statuses.map((item) => [
                            item.caregiver_id,
                            item.availability_status,
                        ]),
                    );
                    for (const id of caregiverIds.slice(start, start + 100)) {
                        const status = shiftStatuses.get(id);
                        if (!status) {
                            omittedIds.add(id);
                            statuses.delete(id);
                            continue;
                        }
                        if (omittedIds.has(id)) {
                            continue;
                        }
                        const previous = statuses.get(id);
                        if (
                            !previous ||
                            priority[status] > priority[previous]
                        ) {
                            statuses.set(id, status);
                        }
                    }
                }
            }
            return statuses;
        },
        { shouldRetryOnError: false, keepPreviousData: false },
    );
    return {
        ...query,
        statuses: useMemo(
            () =>
                waiting
                    ? new Map<
                          string,
                          WorkforceMapPoint['availability_status']
                      >()
                    : (query.data ??
                      new Map<
                          string,
                          WorkforceMapPoint['availability_status']
                      >()),
            [waiting, query.data],
        ),
        isLoading:
            shiftIds.length > 0 &&
            caregiverIds.length > 0 &&
            (waiting || query.isLoading),
    };
}
